import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Archive, Check, RefreshCw, X, ExternalLink, Bell, Loader, CheckCircle, AlertCircle, MessageSquare, Key,
  Calendar, FileText, Settings, Lock, XCircle, Trash2, ClipboardCheck, FilePen, CalendarCheck



 } from 'lucide-react';
import { API_URL } from '../config/apiConfig';

// Le compteur de la cloche n'est plus instancié ici : il vit une seule fois
// dans AdministrationLayout (useNotificationCounter), qui passe au tiroir le
// nombre de non lues, le repère `dernierId` et de quoi le relire tout de suite.
import ConfirmDialog from '../components/ConfirmDialog';
import { Toast } from '../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
const WINDOWS_1252_BYTE_MAP = {
  '\u20AC': 0x80,
  '\u201A': 0x82,
  '\u0192': 0x83,
  '\u201E': 0x84,
  '\u2026': 0x85,
  '\u2020': 0x86,
  '\u2021': 0x87,
  '\u02C6': 0x88,
  '\u2030': 0x89,
  '\u0160': 0x8A,
  '\u2039': 0x8B,
  '\u0152': 0x8C,
  '\u017D': 0x8E,
  '\u2018': 0x91,
  '\u2019': 0x92,
  '\u201C': 0x93,
  '\u201D': 0x94,
  '\u2022': 0x95,
  '\u2013': 0x96,
  '\u2014': 0x97,
  '\u02DC': 0x98,
  '\u2122': 0x99,
  '\u0161': 0x9A,
  '\u203A': 0x9B,
  '\u0153': 0x9C,
  '\u017E': 0x9E,
  '\u0178': 0x9F
};

// Des fonctions stables : une flèche par défaut changerait à chaque rendu et
// relancerait l'effet d'ouverture (relecture de la liste) en boucle.
const SANS_COMPTEUR = () => {};
const SANS_REPERE = () => {};

// `noterNotificationVue(id)` : la coquille relève la référence du son de la
// cloche jusqu'à cet id. Le tiroir l'appelle avant de désarchiver une non lue,
// qui ferait sinon monter le repère `dernierId` comme une arrivée et sonner.
const NotificationModal = ({
  isOpen,
  onClose,
  unreadCount = 0,
  dernierId = null,
  rafraichirCompteur = SANS_COMPTEUR,
  noterNotificationVue = SANS_REPERE
}) => {
  const [currentView, setCurrentView] = useState('all');
  const [notifications, setNotifications] = useState([]); // ✅ Initialiser comme tableau vide
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(new Set()); // IDs des notifications en cours de MAJ

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // ✅ AJOUT: State pour forcer le re-render des temps
  const [, setTimeUpdate] = useState(0);

  // ✅ API BASE URL - GARDÉ tel quel
 const API_BASE_URL = API_URL;
  const repairMojibakeText = useCallback((value) => {
    if (typeof value !== 'string' || !/[ÃÂ]/.test(value)) return value;

    let text = value;
    for (let index = 0; index < 2 && /[ÃÂ]/.test(text); index += 1) {
      try {
        const bytes = Uint8Array.from(Array.from(text), (char) => {
          const code = char.charCodeAt(0);
          return code <= 0xff ? code : WINDOWS_1252_BYTE_MAP[char] || 0x3f;
        });
        const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
        if (!decoded || decoded === text) break;
        text = decoded;
      } catch {
        break;
      }
    }

    return text;
  }, []);

  const normalizeNotificationText = useCallback((notification) => ({
    ...notification,
    title: repairMojibakeText(notification.title),
    message: repairMojibakeText(notification.message)
  }), [repairMojibakeText]);

  const getTenantFromHost = useCallback(() => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'www';
    const parts = hostname.split('.');
    if (parts.length >= 2 && parts[parts.length - 1] === 'localhost') return parts[0];
    return parts.length >= 3 ? parts[0] : null;
  }, []);

  const getNotificationHeaders = useCallback(() => {
    const headers = {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };
    const tenant = getTenantFromHost();
    const anneeScolaireId = localStorage.getItem('annee_scolaire_id');
    if (tenant) headers['X-Tenant'] = tenant;
    if (anneeScolaireId) headers['X-Annee-Scolaire-Id'] = anneeScolaireId;
    return headers;
  }, [getTenantFromHost]);

  // ================================
  // API CALLS - GARDÉ IDENTIQUE
  // ================================

  /**
   * Récupérer les notifications depuis l'API
   * `silencieux` : relecture sans sablier ni bandeau d'erreur (arrivée d'une
   * notification pendant que le tiroir est ouvert).
   */
  const fetchNotifications = useCallback(async ({ silencieux = false } = {}) => {
    if (!silencieux) {
      setLoading(true);
      setError(null);
    }

    try {
      const includeArchived = currentView === 'archived';
      // ✅ URL CORRIGÉE
      const response = await fetch(`${API_BASE_URL}/notifications?includeRead=true&includeArchived=${includeArchived}&limit=50`, {
        headers: getNotificationHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // ✅ S'assurer que c'est bien un tableau
        const notificationsList = Array.isArray(data.data) ? data.data : [];
        setNotifications(notificationsList.map(normalizeNotificationText));
        console.log(`📊 Notifications chargées: ${notificationsList.length}`);
      } else {
        throw new Error(data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('❌ Erreur fetchNotifications:', err);
      if (!silencieux) setError(err.message);
    } finally {
      if (!silencieux) setLoading(false);
    }
  }, [currentView, API_BASE_URL, getNotificationHeaders, normalizeNotificationText]);

  /**
   * Relire le compteur de la cloche : celui de la coquille, partagé avec la
   * pastille, qui se met donc à jour en même temps.
   */
  const fetchUnreadCount = useCallback(() => {
    rafraichirCompteur();
  }, [rafraichirCompteur]);

  /**
   * Marquer une notification comme lue/non lue
   */
  

  /**
   * Archiver/désarchiver une notification
   */
  const toggleArchive = async (notificationId, event) => {
    event.stopPropagation();
    
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;
    
    const newArchivedState = !notification.is_archived;
    setUpdating(prev => new Set(prev).add(notificationId));

    // Désarchiver une non lue la remet dans le compteur, et peut faire monter
    // le repère de la cloche : ce n'est pas une arrivée, la référence du son
    // est relevée AVANT l'envoi, pour qu'aucun relevé du compteur (même un tour
    // de sondage déjà parti) ne la fasse sonner. Sans dommage si l'envoi
    // échoue : les ids ne font que croître, une notification qui arrive
    // ensuite a un id plus grand et sonnera.
    if (!newArchivedState && !notification.is_read) noterNotificationVue(notificationId);

    try {
      // ✅ URL CORRIGÉE
      const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/archive`, {
        method: 'PATCH',
        headers: getNotificationHeaders(),
        body: JSON.stringify({ isArchived: newArchivedState })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de l\'archivage');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Retirer la notification de la liste actuelle si elle change de statut
        if (currentView === 'archived' && !newArchivedState) {
          // Notification désarchivée, la retirer de la vue archives
          setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } else if (currentView === 'all' && newArchivedState) {
          // Notification archivée, la retirer de la vue principale
          setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } else {
          // Mettre à jour sur place
          setNotifications(prev =>
            prev.map(notif =>
              notif.id === notificationId
                ? { ...notif, is_archived: newArchivedState }
                : notif
            )
          );
        }
        
        console.log(`✅ Notification ${notificationId} ${newArchivedState ? 'archivée' : 'désarchivée'}`);
        // Archiver une non lue la retire du compteur (et la désarchiver l'y remet)
        fetchUnreadCount();
      }
    } catch (err) {
      console.error('❌ Erreur toggleArchive:', err);
      setError('Erreur lors de l\'archivage de la notification');
    } finally {
      setUpdating(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };
  
  /**
 * Demander confirmation pour supprimer une notification
 */
const handleDeleteClick = (notificationId, event) => {
  event.stopPropagation();
  setNotificationToDelete(notificationId);
  setShowDeleteDialog(true);
};

/**
 * Supprimer définitivement une notification (après confirmation)
 */
// ✅ MODIFIÉ: Améliorer la gestion du toast
const deleteNotification = async () => {
  if (!notificationToDelete) return;
  
  setUpdating(prev => new Set(prev).add(notificationToDelete));
  
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/${notificationToDelete}`, {
      method: 'DELETE',
      headers: getNotificationHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de la suppression');
    }
    
    const data = await response.json();
    
    if (data.success) {
      // Retirer la notification de la liste
      setNotifications(prev => prev.filter(n => n.id !== notificationToDelete));
      
      // ✅ Afficher le toast
      setToastMessage('Notification supprimée définitivement');
      setShowToast(true);
      
      console.log(`🗑️ Notification ${notificationToDelete} supprimée définitivement`);
    }
  } catch (err) {
    console.error('❌ Erreur deleteNotification:', err);
    setError('Erreur lors de la suppression de la notification');
  } finally {
    setUpdating(prev => {
      const newSet = new Set(prev);
      newSet.delete(notificationToDelete);
      return newSet;
    });
    
    // ✅ Nettoyer les états
    setShowDeleteDialog(false);
    setNotificationToDelete(null);
  }
};

/**
 * Annuler la suppression
 */
const cancelDelete = () => {
  setShowDeleteDialog(false);
  setNotificationToDelete(null);
};


  /**
 * Gérer le clic sur une notification (redirection + marquer comme lue)
 * ✅ VERSION CORRIGÉE POUR REACT ROUTER
 */
const handleNotificationClick = async (notification) => {
  console.log(`🔗 Clic sur notification ${notification.id}:`, notification);
  
  // Marquer comme lue si pas encore lue
  if (!notification.is_read) {
    try {
      await fetch(`${API_BASE_URL}/notifications/${notification.id}/read`, {
        method: 'PATCH',
        headers: getNotificationHeaders(),
        body: JSON.stringify({ isRead: true })
      });
      
      // Mettre à jour localement
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notification.id
            ? { ...notif, is_read: true }
            : notif
        )
      );
      
      console.log(`✅ Notification ${notification.id} marquée comme lue`);
      fetchUnreadCount();
    } catch (err) {
      console.error('❌ Erreur lors du marquage comme lue:', err);
    }
  }
  
  // ✅ NAVIGATION AVEC REACT ROUTER
  if (notification.action_url) {
    console.log(`🔗 Redirection vers: ${notification.action_url}`);
    
    // Fermer le modal d'abord
    onClose();
    
    // Navigation avec React Router
    setTimeout(() => {
      if (notification.action_url.startsWith('http')) {
        // URL externe
        window.open(notification.action_url, '_blank');
      } else {
        // ✅ URL interne - Navigation React Router
        // Extraire l'ID de la tâche de l'URL si présent
        const urlMatch = notification.action_url.match(/\/taches\/(\d+)/) || 
                        notification.action_url.match(/task=(\d+)/);
        
        if (urlMatch) {
          const taskId = urlMatch[1];
          // Naviguer vers les tâches avec un paramètre pour mettre en évidence
          window.location.href = `/administration/taches?highlight=${taskId}`;
        } else if (notification.action_url.includes('/taches')) {
          // Simple redirection vers les tâches
          window.location.href = '/administration/taches';
        } else {
          // Autre URL interne
          window.location.href = notification.action_url;
        }
      }
    }, 200);
  } else {
    // Pas d'URL, juste fermer le modal
    onClose();
  }
};

  /**
   * Marquer toutes les notifications comme lues
   */
  const markAllAsRead = async () => {
    setLoading(true);
    
    try {
      // ✅ URL CORRIGÉE
      const response = await fetch(`${API_BASE_URL}/notifications/mark-all-read`, {
        method: 'POST',
        headers: getNotificationHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du marquage');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Mettre à jour toutes les notifications comme lues
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, is_read: true }))
        );
        
        // La pastille suit tout de suite, sans attendre le tour de sondage
        fetchUnreadCount();
        console.log(`✅ ${data.data?.affectedCount} notifications marquées comme lues`);
      }
    } catch (err) {
      console.error('❌ Erreur markAllAsRead:', err);
      setError('Erreur lors du marquage des notifications');
    } finally {
      setLoading(false);
    }
  };

  // ================================
  // ✅ FONCTIONS D'AFFICHAGE - NETTOYÉES
  // ================================
  
  const getTimeAgo = useCallback((notif) => {
    // Utiliser updated_at si différent de created_at (notification modifiée)
    // Sinon utiliser created_at (notification jamais touchée)
    const referenceDate = notif.updated_at && notif.updated_at !== notif.created_at 
      ? new Date(notif.updated_at)  // Dernière activité
      : new Date(notif.created_at); // Date de création
      
    const now = new Date();
    const diffInSeconds = Math.floor((now - referenceDate) / 1000);
    const diffInDays = Math.floor(diffInSeconds / 86400);
    
    // Après 7 jours : date normale
    if (diffInDays >= 7) {
      return referenceDate.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
    }
    
    // Temps relatif
    if (diffInSeconds < 60) return `il y a ${Math.max(1, diffInSeconds)}s`;
    if (diffInSeconds < 3600) return `il y a ${Math.floor(diffInSeconds / 60)}min`;
    if (diffInSeconds < 86400) return `il y a ${Math.floor(diffInSeconds / 3600)}h`;
    
    // 1-6 jours
    if (diffInDays === 1) return 'Hier';
    return `il y a ${diffInDays} jours`;
  }, []);

  // ✅ COULEURS NETTOYÉES - Types réels uniquement
  const getPriorityColor = (priority, type, title) => {
    // ✅ TES COULEURS selon le type
    if (type === 'support_request') {
      return 'from-[#319190] to-[#63b5b4]'; // 🔔 Nouvelle demande (Turquoise)
    }
    if (type === 'support_response') {
      // Déterminer si accepté ou refusé selon le titre (mots-clés au lieu d'emojis)
      if (title && (title.toLowerCase().includes('approuvée') || title.toLowerCase().includes('acceptée') || title.toLowerCase().includes('validée'))) {
        return 'from-[#246990] to-[#3084b5]'; // ✅ Demande acceptée (Bleu)
      } else if (title && (title.toLowerCase().includes('refusée') || title.toLowerCase().includes('rejetée') || title.toLowerCase().includes('annulée'))) {
        return 'from-[#c66d59] to-[#ae7467]'; // ❌ Demande refusée (Rouge/Orange)
      }
      return 'from-[#246990] to-[#3084b5]'; // Par défaut bleu
    }
    if (type === 'access_response') {
      // Même logique pour les réponses d'accès
      if (title && (title.toLowerCase().includes('approuvée') || title.toLowerCase().includes('acceptée'))) {
        return 'from-[#246990] to-[#3084b5]'; // ✅ Accès approuvé (Bleu)
      } else if (title && (title.toLowerCase().includes('refusée') || title.toLowerCase().includes('rejetée'))) {
        return 'from-[#c66d59] to-[#ae7467]'; // ❌ Accès refusé (Rouge/Orange)
      }
      return 'from-[#246990] to-[#3084b5]'; // Par défaut bleu
    }
  
    // Couleurs par priorité (comportement existant)
    switch (priority) {
      case 'urgent': return 'from-red-500 to-red-600';
      case 'high': return 'from-orange-500 to-orange-600';
      case 'normal': return 'from-[#c66d59] to-[#ae7467]';
      case 'low': return 'from-blue-500 to-blue-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  // ✅ LABELS NETTOYÉS - Types réels uniquement
  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case 'support_request':
        return 'Demande de support';
      case 'support_response':
        return 'Réponse du support';
      case 'access_response':
        return 'Réponse d\'accès';
      case 'absence_request':
        return 'Demande d\'absence';
      case 'note_added':
        return 'Note ajoutée';
      case 'system_maintenance':
        return 'Maintenance système';
      case 'password_reset':
        return 'Réinitialisation mot de passe';
      case 'task_assigned':
        return 'Tâche assignée';
      case 'task_group_assigned':
        return 'Tâche groupe';
      case 'task_group_info':
        return 'Info groupe';
      case 'task_status_change':
        return 'Changement statut';
      // Notes saisies par les enseignants depuis leur espace (validationNotes)
      case 'notes_enseignant_soumission':
        return 'Notes à valider';
      case 'notes_enseignant_rectification':
        return 'Rectification de notes';
      // Au primaire, une cloche par classe et par mois (pas une par matière) :
      // le mois se valide en un geste, sur la page du mois de la classe.
      case 'notes_enseignant_mois':
        return 'Mois de notes à valider';
      default:
        // Un autre type du même circuit, que cette liste ne connaît pas encore,
        // se reconnaît à son préfixe plutôt que de passer pour « Notification ».
        if (typeof type === 'string' && type.startsWith('notes_enseignant_mois')) return 'Mois de notes à valider';
        if (typeof type === 'string' && type.startsWith('notes_enseignant')) return 'Notes des enseignants';
        return 'Notification';
    }
  };

  // ✅ Fonction pour nettoyer les titres des anciens emojis
const cleanTitle = (title) => {
  if (!title) return title;
  return title.replace(/^[✅❌]\s*/, ''); // Retire ✅ ou ❌ au début du titre
};

  // ✅ ICÔNES NETTOYÉES - Types réels uniquement
  const getNotificationIcon = (type, title, isRead) => {
    // ✅ Couleur selon l'état de lecture
    const iconColor = isRead ? "text-gray-600" : "text-white";
    
    // ✅ Détection par mots-clés (forme féminine pour tes titres)
    if (title && (title.toLowerCase().includes('approuvée') || title.toLowerCase().includes('acceptée') || title.toLowerCase().includes('validée'))) {
      return <CheckCircle className={`w-5 h-5 ${iconColor}`} />;
    }
    if (title && (title.toLowerCase().includes('refusée') || title.toLowerCase().includes('rejetée') || title.toLowerCase().includes('annulée'))) {
      return <XCircle className={`w-5 h-5 ${iconColor}`} />;
    }
    
    // Icônes Lucide selon le type
    switch (type) {
      case 'support_request':
        return <AlertCircle className={`w-5 h-5 ${iconColor}`} />;
      case 'support_response':
        return <MessageSquare className={`w-5 h-5 ${iconColor}`} />;
      case 'access_response':
        return <Key className={`w-5 h-5 ${iconColor}`} />;
      case 'absence_request':
        return <Calendar className={`w-5 h-5 ${iconColor}`} />;
      case 'note_added':
        return <FileText className={`w-5 h-5 ${iconColor}`} />;
      case 'system_maintenance':
        return <Settings className={`w-5 h-5 ${iconColor}`} />;
      case 'password_reset':
        return <Lock className={`w-5 h-5 ${iconColor}`} />;
        case 'task_assigned':
      case 'task_group_assigned':
      case 'task_group_info':
        return <FileText className={`w-5 h-5 ${iconColor}`} />;
      case 'task_status_change':
  return <CheckCircle className={`w-5 h-5 ${iconColor}`} />;
      // Notes saisies par les enseignants : une feuille à valider, une
      // rectification demandée sur une feuille déjà validée.
      case 'notes_enseignant_soumission':
        return <ClipboardCheck className={`w-5 h-5 ${iconColor}`} />;
      case 'notes_enseignant_rectification':
        return <FilePen className={`w-5 h-5 ${iconColor}`} />;
      case 'notes_enseignant_mois':
        return <CalendarCheck className={`w-5 h-5 ${iconColor}`} />;
      default:
        if (typeof type === 'string' && type.startsWith('notes_enseignant')) {
          return <ClipboardCheck className={`w-5 h-5 ${iconColor}`} />;
        }
        return <Bell className={`w-5 h-5 ${iconColor}`} />;
    }
  };

  // ✅ COMPOSANT DE RENDU - Unique et propre
  const renderNotificationItem = (notif) => (
    <motion.div 
      key={notif.id}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`group relative p-4 rounded-lg transition-all cursor-pointer ${
        !notif.is_read 
          ? `bg-gradient-to-r ${getPriorityColor(notif.priority, notif.type, notif.title)} text-white`
          : 'bg-gray-50 hover:bg-gray-100'
      }`}
      whileHover={{ scale: 1.01 }}
      onClick={() => handleNotificationClick(notif)}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {/* ✅ ICÔNE selon le type */}
            <div className="flex items-center justify-center mr-1">
            {getNotificationIcon(notif.type, notif.title, notif.is_read)}
          </div>
            <h3 className="font-semibold">{cleanTitle(notif.title)}</h3>
            
            
          </div>
          
          <p className="text-sm mt-1 opacity-90">{notif.message}</p>
          
          
          {/* ✅ Badge du type de notification */}
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-block px-2 py-1 text-xs bg-black/10 rounded">
              {getNotificationTypeLabel(notif.type)}
            </span>
            
            {/* ✅ Badge de priorité pour les demandes importantes */}
            {(notif.priority === 'urgent' || notif.priority === 'high') && notif.type === 'support_request' && (
  <span className="inline-block px-2 py-1 text-xs bg-red-500/20 text-red-800 rounded">
    {notif.priority === 'urgent' ? '🚨 Urgent' : '⚡ Priorité élevée'}
  </span>
)}

            
          </div>
        </div>
        
        <div className="flex items-center">
  <button
    onClick={(e) => toggleArchive(notif.id, e)}
    disabled={updating.has(notif.id)}
    className={`p-1.5 rounded-full transition-colors ${
      !notif.is_read 
        ? 'hover:bg-white/20 text-white' 
        : 'text-gray-400 hover:text-[#246990] hover:bg-gray-100'
    } disabled:opacity-50`}
    title={notif.is_archived ? "Désarchiver" : "Archiver"}
  >
    <Archive size={16} />
  </button>
  
  {/* ✅ NOUVEAU: Bouton suppression uniquement dans les archives */}
  {currentView === 'archived' && (
  <button
    onClick={(e) => handleDeleteClick(notif.id, e)}
    disabled={updating.has(notif.id)}
    className={`p-1.5 rounded-full transition-colors ${
      !notif.is_read 
        ? 'hover:bg-red-500/20 text-white' 
        : 'text-red-400 hover:text-red-600 hover:bg-red-50'
    } disabled:opacity-50`}
    title="Supprimer définitivement"
  >
    {updating.has(notif.id) ? (
      <Loader size={16} className="animate-spin" />
    ) : (
      <Trash2 size={16} />
    )}
  </button>
)}
</div>


      </div>
    </motion.div>
  );

  // ================================
  // EFFECTS - GARDÉ IDENTIQUE
  // ================================

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      fetchUnreadCount();
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, fetchNotifications, fetchUnreadCount]);

  // Recharger les notifications quand on change de vue
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [currentView, fetchNotifications]);

  // Une notification arrive pendant que le tiroir est ouvert (le repère
  // `dernierId` du compteur monte) : la liste la montre, sans sablier. Sans
  // cela, la cloche sonnait et le tiroir ouvert n'affichait rien de nouveau.
  const dernierIdConnuRef = useRef(dernierId);
  useEffect(() => {
    const precedent = dernierIdConnuRef.current;
    dernierIdConnuRef.current = dernierId;
    // null puis un id : la première non lue depuis l'ouverture du tiroir.
    if (!isOpen || dernierId == null || (precedent != null && dernierId <= precedent)) return;
    fetchNotifications({ silencieux: true });
  }, [dernierId, isOpen, fetchNotifications]);

  // ✅ Timer pour actualiser les temps relatifs
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setTimeUpdate(prev => prev + 1); // Force le re-render pour mettre à jour les temps
    }, 60000); // Toutes les minutes
    
    return () => clearInterval(interval);
  }, [isOpen]);

  // ================================
  // RENDER - NETTOYÉ
  // ================================

  if (!isOpen) return null;

  // Filtrer les notifications selon la vue
  const filteredNotifications = Array.isArray(notifications) 
    ? notifications.filter(notif => 
        currentView === 'archived' ? notif.is_archived : !notif.is_archived
      )
    : [];

    const notificationsCount = Array.isArray(notifications) 
  ? notifications.filter(notif => !notif.is_archived).length 
  : 0;

const archivedCount = Array.isArray(notifications) 
  ? notifications.filter(notif => notif.is_archived).length 
  : 0;


  return (
    <div className="fixed inset-0 flex items-start justify-end" style={{ zIndex: 1000 }}>
      <div 
        className="fixed inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="relative w-full max-w-md h-screen bg-white shadow-xl flex flex-col"
      >
        {/* En-tête */}
        <div className="flex justify-between items-center p-4 border-b bg-white">
          <div className="flex items-center gap-2">
            <Bell className="text-[#246990]" size={20} />
            <h2 className="text-xl font-semibold text-[#246990]">Notifications</h2>
          </div>
          <div className="flex items-center gap-2">
            {currentView === 'all' && (
              <button
                onClick={markAllAsRead}
                disabled={loading || unreadCount === 0}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
              >
                Tout lire
              </button>
            )}
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="text-gray-500 hover:text-gray-700" size={20} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex gap-2 p-3 bg-white/80 border-b">
    <button
      onClick={() => setCurrentView('all')}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
        ${currentView === 'all' ? 'bg-[#246990] text-white' : 'hover:bg-gray-100'}`}
    >
      <Check size={16} />
      Notifications 
      <span className={`ml-1 px-2 py-0.5 text-xs rounded-lg ${
        currentView === 'all' 
          ? 'bg-white/20 text-white' 
          : 'bg-gray-200 text-gray-600'
      }`}>
        {notificationsCount}
      </span>
    </button>
    
    <button
      onClick={() => setCurrentView('archived')}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
        ${currentView === 'archived' ? 'bg-[#246990] text-white' : 'hover:bg-gray-100'}`}
    >
      <Archive size={16} />
      Archives 
      <span className={`ml-1 px-2 py-0.5 text-xs rounded-lg ${
        currentView === 'archived' 
          ? 'bg-white/20 text-white' 
          : 'bg-gray-200 text-gray-600'
      }`}>
        {archivedCount}
      </span>
    </button>
  </div>

        {/* Messages d'erreur */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-red-50 border-l-4 border-red-400 p-3"
            >
              <p className="text-red-700 text-sm">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 text-xs mt-1"
              >
                Masquer
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Liste des notifications */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-[#246990]" size={24} />
              <span className="ml-2 text-gray-600">Chargement...</span>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <AnimatePresence>
                {/* ✅ UTILISE SEULEMENT renderNotificationItem */}
                {filteredNotifications.map((notif) => renderNotificationItem(notif))}
              </AnimatePresence>

              {!loading && filteredNotifications.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <Bell size={48} className="mx-auto mb-3 opacity-30" />
                  <p>
                    {currentView === 'archived' 
                      ? "Aucune notification archivée"
                      : "Aucune notification"
                    }
                  </p>
                </div>
              )}
            </div>
            
          )}
          
        </div>

        
      </motion.div>

      <ConfirmDialog
  isOpen={showDeleteDialog}
  onConfirm={deleteNotification}
  onClose={cancelDelete}  // ✅ CORRIGÉ: utilise cancelDelete pour onClose
  title="Supprimer la notification"
  message="Êtes-vous sûr de vouloir supprimer définitivement cette notification ? Cette action est irréversible."
  confirmText="Supprimer"
  cancelText="Annuler"
  type="danger"
  style={{ zIndex: 2000 }}
/>
    
{showToast && (
  <Toast
    message={toastMessage}
    type="success"
    onClose={() => {
      setShowToast(false);
      setToastMessage('');
    }}
  />
)}

      
    </div>

          

  );

  
};

export default NotificationModal;
